import type { Message } from '@zenmako/contracts/threads';
import { useEffect, useRef, useState, useCallback } from 'preact/hooks';
import { ToolCallRenderer } from './ToolCallRenderer';
import { renderMarkdown } from '../../../utils/markdown';
import { toast } from 'react-toastify';

interface MessageListProps {
  messages: Message[];
  agentName: string;
  threadId: string;
  loading?: boolean;
}

const THINKING_QUOTES = [
  "Consulting the oracle...",
  "Summoning electrons...",
  "Thinking recursively...",
  "Wrangling bits...",
  "Herding neurons..."
];

export function MessageList({ messages, agentName, threadId, loading }: MessageListProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showThinking, setShowThinking] = useState(false);
  const [thinkingQuote] = useState(() => THINKING_QUOTES[Math.floor(Math.random() * THINKING_QUOTES.length)]);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [pinnedIds, setPinnedIds] = useState<Set<string>>(new Set());

  const getPlainTextContent = useCallback((message: Message): string => {
    const content = typeof message.content === 'string'
      ? message.content
      : message.content?.filter(c => c.type === 'text').map(c => c.text).join('') || '';
    return content;
  }, []);

  const handleCopy = useCallback(async (message: Message) => {
    const text = getPlainTextContent(message);
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(message.id);
      toast.success('Copied to clipboard', { autoClose: 1500 });
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  }, [getPlainTextContent]);

  const handlePin = useCallback((messageId: string) => {
    setPinnedIds(prev => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
        toast.info('Unpinned message', { autoClose: 1500 });
      } else {
        next.add(messageId);
        toast.success('Pinned message', { autoClose: 1500 });
      }
      return next;
    });
  }, []);

  // Message action buttons component
  const MessageActions = ({ message, isUser }: { message: Message; isUser: boolean }) => {
    const isCopied = copiedId === message.id;
    const isPinned = pinnedIds.has(message.id);

    return (
      <div class={`flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ${isUser ? 'mr-2' : 'ml-2'}`}>
        {/* Copy button */}
        <button
          onClick={() => handleCopy(message)}
          class="btn btn-ghost btn-xs btn-square hover:bg-base-content/10"
          title="Copy message"
        >
          {isCopied ? (
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 text-success" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          )}
        </button>

        {/* Pin button */}
        <button
          onClick={() => handlePin(message.id)}
          class={`btn btn-ghost btn-xs btn-square hover:bg-base-content/10 ${isPinned ? 'text-warning' : ''}`}
          title={isPinned ? 'Unpin message' : 'Pin message'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill={isPinned ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
          </svg>
        </button>
      </div>
    );
  };

  useEffect(() => {
    // Scroll to bottom when messages change or loading state changes
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, [messages, showThinking]);

  useEffect(() => {
    // Debounce loading indicator by 200ms
    if (loading) {
      const timer = setTimeout(() => setShowThinking(true), 200);
      return () => clearTimeout(timer);
    } else {
      setShowThinking(false);
    }
  }, [loading]);

  const renderMessageContent = (message: Message) => {
    const content = typeof message.content === 'string'
      ? message.content
      : message.content?.filter(c => c.type === 'text').map(c => c.text).join('') || '';

    // Render markdown using marked + DOMPurify
    return renderMarkdown(content);
  };

  const formatTime = (date: Date | string) => {
    const d = new Date(date);
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatFullDateTime = (date: Date | string) => {
    const d = new Date(date);
    return d.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (messages.length === 0) {
    return (
      <div ref={scrollContainerRef} class="flex-1 overflow-y-auto px-4 py-6">
        <div class="w-full">
          <div class="hero min-h-[40vh]">
            <div class="hero-content text-center">
              <div class="max-w-md">
                <h1 class="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  Welcome to Zenmako!
                </h1>
                <p class="py-6">
                  Start a conversation with {agentName}. Ask questions, get help, or explore what I can do.
                </p>
                <div class="flex flex-wrap gap-2 justify-center">
                  <div class="badge badge-outline">💡 Ask questions</div>
                  <div class="badge badge-outline">🔧 Use tools</div>
                  <div class="badge badge-outline">📝 Get assistance</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={scrollContainerRef} class="flex-1 overflow-y-auto px-4 py-6">
      <div class="w-full space-y-4">
        {messages.map((message) => (
          <div key={message.id} class="mb-4">
            {message.role === 'user' ? (
              <div class="flex justify-end items-start group">
                <MessageActions message={message} isUser={true} />
                <div class="max-w-[85%]">
                  <div class={`bg-base-300/70 text-base-content rounded-2xl px-4 py-2.5 ${pinnedIds.has(message.id) ? 'ring-2 ring-warning/50' : ''}`}>
                    <div
                      class="text-sm max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
                      dangerouslySetInnerHTML={{ __html: renderMessageContent(message) }}
                    />
                  </div>
                  <div class="text-xs text-base-content/40 mt-1.5 text-right">
                    {pinnedIds.has(message.id) && <span class="text-warning mr-1">📌</span>}
                    {(message.metadata?.user?.email || message.metadata?.user?.username) && `${message.metadata.user.email || message.metadata.user.username} · `}
                    <span class="tooltip tooltip-left" data-tip={formatFullDateTime(message.timestamp)}>
                      {formatTime(message.timestamp)}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div class="flex flex-col justify-start w-full group">
                {/* Tool calls - full width */}
                {message.toolCalls && message.toolCalls.length > 0 && (
                  <div class="mb-2 space-y-2 w-full">
                    {message.toolCalls
                      .map((toolCall) => {
                        return (
                          <ToolCallRenderer
                            key={toolCall.id}
                            toolCall={{
                              ...toolCall as any,
                              timestamp: typeof message.timestamp === 'string' ? message.timestamp : message.timestamp.toISOString()
                            }}
                            threadId={threadId}
                            messageId={message.id}
                          />
                        );
                      })
                    }
                  </div>
                )}

                {/* Message content - constrained width */}
                {renderMessageContent(message) && (
                  <div class="flex items-start">
                    <div class="max-w-[90%]">
                      {/* A message flagged as an error reports why the run could
                          not happen — a spent API key, a revoked credential. It is
                          not something the agent said, so it must not look like it. */}
                      <div class={`${message.metadata?.error
                          ? 'bg-error/10 text-base-content border border-error/30'
                          : 'bg-base-200/50 text-base-content'} rounded-2xl px-4 py-2.5 ${pinnedIds.has(message.id) ? 'ring-2 ring-warning/50' : ''}`}>
                        {message.metadata?.error && (
                          <div class="flex items-center gap-1.5 text-error text-xs font-medium mb-1.5">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" class="h-3.5 w-3.5">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <span>Couldn't run</span>
                          </div>
                        )}
                        <div
                          class="text-sm max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
                          dangerouslySetInnerHTML={{ __html: renderMessageContent(message) }}
                        />
                      </div>
                      <div class="text-xs text-base-content/40 mt-1.5">
                        {pinnedIds.has(message.id) && <span class="text-warning mr-1">📌</span>}
                        {agentName} · <span class="tooltip tooltip-right" data-tip={formatFullDateTime(message.timestamp)}>
                          {formatTime(message.timestamp)}
                        </span>
                      </div>
                    </div>
                    <MessageActions message={message} isUser={false} />
                  </div>
                )}

                {/* Timestamp for tool-only messages */}
                {(!renderMessageContent(message)) && (
                  <div class="text-xs text-base-content/40 mt-1.5">
                    {agentName} · <span class="tooltip tooltip-right" data-tip={formatFullDateTime(message.timestamp)}>
                      {formatTime(message.timestamp)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Thinking indicator */}
        {showThinking && (
          <div class="mb-4">
            <div class="flex justify-start">
              <div class="max-w-[90%]">
                <div class="bg-base-200/50 text-base-content rounded-2xl px-4 py-3 flex items-center gap-3">
                  <span class="loading loading-dots loading-sm text-base-content/50"></span>
                  <span class="text-sm text-base-content/60">{thinkingQuote}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
